import { Alert, Autocomplete, Button, FormControl, FormHelperText, Stack, TextField, Tooltip } from '@mui/material';
import React, { useEffect, useState, useTransition } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { isLegacyOp, removeOpSuffix } from '../../utils/operations';
import { sortArr } from '../../utils/string';
import { callAPIAsync, getAllOperations } from '../../utils/api';
import { switchTab } from '../../store/tab/tabSlice';
import { DoubleArrow } from '@mui/icons-material';
import { useLogto } from '@logto/react';
import { SnackbarAlert } from '../SnackbarAlert';

interface OpProps {
	selectedOption: string;
	setSelectedOption: (op: string) => void;
	operationType: 'UPLOAD' | 'MANAGE' | 'VIEW';
	topTextOverride?: string;
	loadAll?: boolean;
	showCreateShortcut?: boolean;
}

const OperationDropdown: React.FC<OpProps> = ({
	selectedOption,
	setSelectedOption,
	operationType,
	topTextOverride,
	loadAll,
	showCreateShortcut,
}) => {
	const dispatch = useAppDispatch();
	const logto = useLogto();

	const allowedOptions = useAppSelector((state) => state.user.roles);
	const uploadContainer = useAppSelector((state) => state.upload.fileOperation);
	const userRoles = useAppSelector((state) => state.tab.userRoles);

	const [rawOperations, setRawOperations] = useState<string[]>(allowedOptions);
	const [opsToDisplay, setOpsToDisplay] = useState<string[]>([]);
	const [loadWarning, setLoadWarning] = useState<string>('');

	const [opsLoading, startTransition] = useTransition();

	const isAdmin = userRoles.includes('ADMINISTRATOR') || userRoles.includes('SUPERUSER');

	let additionalHelperText = 'This list is populated from the operations that you currently have control over';
	if (!selectedOption) {
		additionalHelperText += ', the rest of the form will be enabled once this has been selected';
	}

	// removes '-upload' and/or '-view' from op names, ensures '-view' ops don't appear in upload dropdowns
	const adjustedOps = (opsToAdjust: string[]): string[] => {
		const adjustedOperations: string[] = [];
		opsToAdjust.map((option) => {
			if (option.endsWith('-upload') && (operationType === 'UPLOAD' || operationType === 'MANAGE')) {
				adjustedOperations.push(removeOpSuffix(option));
			} else if (option.endsWith('-view') && (operationType === 'VIEW' || operationType === 'MANAGE')) {
				adjustedOperations.push(removeOpSuffix(option));
			} else if ((isLegacyOp(option) && option !== 'DEFAULT-OPERATION') || (option === 'DEFAULT-OPERATION' && isAdmin)) {
				adjustedOperations.push(option);
			}
		});
		if (adjustedOperations.length === 0 && !isAdmin && operationType !== 'MANAGE') {
			adjustedOperations.push('DEFAULT-OPERATION');
		}
		return adjustedOperations;
	};

	const getOperationTooltip = (): string => {
		let tooltip = '';
		if (opsLoading) {
			tooltip = 'Your list of operations is loading, please wait';
		} else if (opsToDisplay.length == 0) {
			tooltip = 'You do not have any assigned operations. Please contact your CO if you think this is a mistake';
		} else if (uploadContainer) {
			tooltip = 'Please cancel your current upload before changing the selected operation';
		}
		return tooltip;
	};

	const handleSetSelectedOption = (option: string) => {
		// if the op has '-upload' suffixed then we assume there is an equivalent with '-view'
		if (rawOperations.includes(`${option}-upload`) || rawOperations.includes(`${option}-view`)) {
			setSelectedOption(`${option}-upload`);
		} else {
			setSelectedOption(option);
		}
	};

	const handleSelectedOption = (option: string): string => {
		if (option.endsWith('-upload')) {
			return removeOpSuffix(option);
		} else {
			return option;
		}
	};

	const updateDisplayOps = (rawOps: string[]) => {
		const uniqueOps = Array.from(new Set(adjustedOps(rawOps)));
		setOpsToDisplay(sortArr(uniqueOps));
	};

	const getOperations = () => {
		if (loadAll) {
			startTransition(async () => {
				await callAPIAsync(logto, async (token) => {
					const res = await getAllOperations(token);
					if (res.ok) {
						if (res.status === 206) {
							setLoadWarning('Some Operations may have failed to load');
						}
						const ops = (await res.json()) as string[];
						if (ops.length > 0) {
							setRawOperations(ops);
							updateDisplayOps(ops);
						}
					}
				});
			});
		} else {
			startTransition(() => {
				setRawOperations(allowedOptions);
				updateDisplayOps(allowedOptions);
			});
		}
	};

	useEffect(() => {
		getOperations();
	}, [allowedOptions, isAdmin]);

	return (
		<Stack direction="row" width={'100%'}>
			<FormControl fullWidth variant="outlined" disabled={opsToDisplay.length === 0}>
				<Tooltip title={getOperationTooltip()}>
					<span>
						<Autocomplete
							value={handleSelectedOption(selectedOption)}
							onChange={(_, newVal) => {
								handleSetSelectedOption(newVal || '');
							}}
							options={opsToDisplay}
							fullWidth
							disabled={Boolean(uploadContainer) || opsLoading}
							renderInput={(params) => (
								<TextField
									{...params}
									slotProps={{
										htmlInput: { ...params.inputProps, 'aria-label': 'Operation ID' },
									}}
									label={opsLoading ? 'Loading...' : 'Operation ID'}
									required
								/>
							)}
							data-testid="op-selector"
						/>
					</span>
				</Tooltip>
				<FormHelperText>{topTextOverride || 'Choose an operation to attach this file to'}</FormHelperText>
				<FormHelperText>{additionalHelperText}</FormHelperText>
				{selectedOption === 'DEFAULT-OPERATION' && (
					<Alert data-testid="default-op-warning" severity="warning">
						The default operation is for training purposes only and cannot be uploaded to.
					</Alert>
				)}
			</FormControl>

			{showCreateShortcut && (
				<Button
					variant="contained"
					color="primary"
					sx={{ height: '56px', marginLeft: 1, marginBottom: '25px' }}
					onClick={() => {
						dispatch(switchTab('Management.Create Operation'));
					}}
					aria-label={'Link to Create Operation page'}
					data-testid="link-to-create-op-button"
				>
					{<DoubleArrow />}
					&nbsp;Create
				</Button>
			)}
			<SnackbarAlert severity="warning" open={Boolean(loadWarning)} onClose={() => setLoadWarning('')} alertMessage={loadWarning} />
		</Stack>
	);
};

export default OperationDropdown;
